import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { restore, session } from '@/lib/session';
import { trackPageView } from '@/lib/analytics';

const routes: RouteRecordRaw[] = [
  {
    // Signed out, the front door is the marketing page rather than a login form.
    // Signed in, it is the dashboard — nobody wants to be re-sold a product they
    // are already paying for.
    path: '/',
    name: 'landing',
    component: () => import('@/views/LandingView.vue'),
    beforeEnter: () => (session.isAuthenticated.value ? '/dashboard' : true),
  },
  { path: '/login', name: 'login', component: () => import('@/views/LoginView.vue') },
  { path: '/signup', name: 'signup', component: () => import('@/views/SignupView.vue') },
  { path: '/verify', name: 'verify', component: () => import('@/views/VerifyView.vue') },
  { path: '/forgot', name: 'forgot', component: () => import('@/views/ForgotView.vue') },
  { path: '/reset', name: 'reset', component: () => import('@/views/ResetView.vue') },
  {
    path: '/build',
    name: 'build',
    component: () => import('@/views/BuildView.vue'),
    meta: { requiresAuth: true },
  },
  {
    /**
     * One frame, four sections. The dashboard used to be a single scrolling
     * page holding the balance, the sites, the packs, the password form, the
     * ledger and the invoices in the order they happened to be written.
     */
    path: '/dashboard',
    component: () => import('@/views/dashboard/DashboardLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/dashboard/OverviewView.vue'),
        meta: { title: 'Overview' },
      },
      {
        path: 'sites',
        name: 'dashboard-sites',
        component: () => import('@/views/dashboard/SitesView.vue'),
        meta: { title: 'Sites' },
      },
      {
        path: 'videos',
        name: 'dashboard-videos',
        component: () => import('@/views/dashboard/VideosView.vue'),
        meta: { title: 'Video backgrounds' },
      },
      {
        path: 'billing',
        name: 'dashboard-billing',
        component: () => import('@/views/dashboard/BillingView.vue'),
        meta: { title: 'Billing' },
      },
      {
        path: 'account',
        name: 'dashboard-account',
        component: () => import('@/views/dashboard/AccountView.vue'),
        meta: { title: 'Account' },
      },
      {
        // Guarded by the API, not by the router: every endpoint behind it 403s
        // unless the caller is on ADMIN_EMAILS. A client-side check would only
        // hide the link, which is decoration rather than access control.
        path: 'admin',
        name: 'dashboard-admin',
        component: () => import('@/views/dashboard/AdminView.vue'),
        meta: { title: 'Accounts' },
      },
    ],
  },
  {
    // Public by necessity: the whole point is that the holder is not signed in
    // yet. The token is the credential, and the API is what checks it.
    path: '/enter',
    name: 'enter',
    component: () => import('@/views/EnterView.vue'),
    meta: { title: 'Signing in' },
  },
  { path: '/status', name: 'status', component: () => import('@/views/StatusView.vue') },
  /**
   * The legal documents. Public and unauthenticated by construction — a customer
   * has to be able to read what they are agreeing to before they have an account,
   * and a payment reviewer has to be able to reach them without one at all.
   */
  ...(
    [
      ['terms', 'Terms of Service'],
      ['privacy', 'Privacy Policy'],
      ['refunds', 'Refund Policy'],
      ['acceptable-use', 'Acceptable Use Policy'],
    ] as const
  ).map(([doc, title]) => ({
    path: `/legal/${doc}`,
    name: `legal-${doc}`,
    component: () => import('@/views/LegalView.vue'),
    meta: { doc, title },
  })),
  { path: '/legal', redirect: '/legal/terms' },
  {
    // Only reachable with the fake payment provider, which stands in for the
    // provider-hosted checkout page in local development.
    path: '/dev/checkout',
    name: 'dev-checkout',
    component: () => import('@/views/DevCheckoutView.vue'),
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

export const router = createRouter({ history: createWebHistory(), routes });

router.beforeEach(async (to) => {
  // The session is restored from a cookie asynchronously, so a guard that reads
  // it before that finishes would bounce an authenticated user to /login on
  // every hard refresh.
  if (!session.ready.value) await restore();

  if (to.meta.requiresAuth && !session.isAuthenticated.value) {
    return { name: 'login', query: { next: to.fullPath } };
  }
  if (session.isAuthenticated.value && ['login', 'signup'].includes(String(to.name))) {
    return { name: 'dashboard' };
  }
  return true;
});

/**
 * Recover from a deploy happening under an open tab.
 *
 * Every rebuild renames the hashed chunks, so a tab opened before the deploy
 * asks for files that no longer exist the moment it lazy-loads a route — the
 * user clicks "Sign out" and gets a blank page with a TypeError. The recovery
 * is a full reload of the target URL, which fetches the new index.html and its
 * new chunk names.
 *
 * The sessionStorage flag stops the pathological case: if a chunk is missing
 * for a real reason (broken deploy, half-synced CDN), reloading forever would
 * turn one error into a flickering tab. One retry, then let the error surface.
 */
router.onError((error, to) => {
  const chunkFailed = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
    String(error instanceof Error ? error.message : error),
  );
  if (!chunkFailed) return;

  const key = `chunk-reload:${to.fullPath}`;
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, '1');
  window.location.assign(to.fullPath);
});

// A legal page that says "briefAI" in the tab is one a customer cannot find
// again after opening four of them side by side.
router.afterEach((to) => {
  // A navigation that succeeded clears its retry flag, so the *next* deploy
  // under this tab is allowed its own single reload too.
  sessionStorage.removeItem(`chunk-reload:${to.fullPath}`);

  // A no-op unless analytics is configured *and* this visitor allowed it.
  trackPageView(to.fullPath);

  const title = to.meta.title as string | undefined;
  document.title = title ? `${title} — briefAI` : 'briefAI';
});

export default router;
